// src/api/attendanceApi.js
import axios from 'axios';

const BASE = process.env.REACT_APP_API_BASE_URL || 'https://education-backend.liara.run';
const authHeader = () => {
  const t = localStorage.getItem('teacherToken');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

/**
 * getLessonStudents(lessonId)
 * جواب: هر چیزی که سرور می‌دهد (معمولاً لیست دانش‌آموزان آن درس)
 */
export const getLessonStudents = async (lessonId) => {
  const res = await axios.get(`${BASE}/api/student?lessonId=${encodeURIComponent(lessonId)}`, {
    headers: authHeader()
  });
  return res.data;
};

/**
 * getAttendance(lessonId, date, sessionIndex)
 * جواب: انتظار می‌رود قالب برگشتی همان قالب کنترلر Attendance باشد:
 * { requestId, lessonId, date, sessionIndex, items: [...] }
 */
export const getAttendance = async (lessonId, date, sessionIndex = 1) => {
  const res = await axios.get(
    `${BASE}/api/attendance?lessonId=${encodeURIComponent(lessonId)}&date=${encodeURIComponent(date)}&session=${sessionIndex}`,
    { headers: authHeader() }
  );
  return res.data;
};

/**
 * saveAttendance(payload)
 * payload: BulkAttendanceDto مشابه بک‌اند
 */
export const saveAttendance = async (payload) => {
  const res = await axios.post(
    `${BASE}/api/attendance/bulk-save`,
    payload,
    { headers: { ...authHeader(), 'Content-Type': 'application/json' } }
  );
  return res.data;
};

/**
 * getStudentAttendanceHistory(studentId, from?, to?)
 * جواب: لیست حضورهای دانش‌آموز
 */
export const getStudentAttendanceHistory = async (studentId, from, to) => {
  const q = [];
  if (from) q.push(`from=${encodeURIComponent(from)}`);
  if (to) q.push(`to=${encodeURIComponent(to)}`);
  const qs = q.length ? `?${q.join('&')}` : '';
  const res = await axios.get(`${BASE}/api/attendance/student/${encodeURIComponent(studentId)}${qs}`, {
    headers: authHeader()
  });
  return res.data;
};

/**
 * getAttendanceNote / saveAttendanceNote
 * برداشتن خطاها را در این لایه تا پیام کاربرپسند برگردد
 */
export const getAttendanceNote = async (lessonId, dateIso, sessionIndex = 1) => {
  try {
    const res = await axios.get(
      `${BASE}/api/attendance/note?lessonId=${encodeURIComponent(lessonId)}&date=${encodeURIComponent(dateIso)}&session=${sessionIndex}`,
      { headers: authHeader(), validateStatus: s => s < 500 }
    );
    // انتظار: { note: "..." }
    if (res.status === 200) return res.data;
    if (res.status === 404) return { note: '' };
    throw new Error(res?.data?.message || `getAttendanceNote failed: ${res.status}`);
  } catch (err) {
    const msg = err?.response?.data?.message || err?.message || 'خطا در گرفتن یادداشت دبیر';
    throw new Error(msg);
  }
};

export const saveAttendanceNote = async ({ lessonId, date, sessionIndex, note }) => {
  try {
    const res = await axios.post(
      `${BASE}/api/attendance/note`,
      { lessonId, date, sessionIndex, note },
      { headers: { ...authHeader(), 'Content-Type': 'application/json' }, validateStatus: s => s < 500 }
    );
    if (res.status === 200) return res.data;
    throw new Error(res?.data?.message || `saveAttendanceNote failed: ${res.status}`);
  } catch (err) {
    const msg = err?.response?.data?.message || err?.message || 'خطا در ذخیره یادداشت دبیر';
    throw new Error(msg);
  }
};

/*
  Admin helpers
  این توابع به Admin کمک می‌کنند تا بدون نیاز به لیست درس‌های معلم، داده‌ها را دریافت کنند.
  اگر سرور اندپوینت‌های admin را پیاده نکرده، این توابع پیغام خطا/fallback معقول برمی‌گردانند.
*/

/**
 * getAdminLessons()
 * تلاش می‌کند لیست همهٔ درس‌ها برای Admin بگیرد.
 * انتظار سرور: GET /api/attendance/admin/lessons  یا مسیر مشابه
 * در صورت عدم وجود اندپوینت admin، fallback به /api/lesson/by-teacher انجام می‌شود.
 */
export const getAdminLessons = async () => {
  // مسیر فرضی admin (در صورت متفاوت بودن، این URL را مطابق سرور تغییر بده)
  const adminUrl = `${BASE}/api/attendance/admin/lessons`;
  const fallbackUrl = `${BASE}/api/lesson/by-teacher`; // مسیر فعلی شما
  try {
    const res = await axios.get(adminUrl, { headers: authHeader(), validateStatus: s => s < 500 });
    if (res.status === 200 && Array.isArray(res.data)) return res.data;
    // اگر adminUrl پاسخ مناسب نداد، به fallback برو
    const fb = await axios.get(fallbackUrl, { headers: authHeader(), validateStatus: s => s < 500 });
    return fb.status === 200 ? fb.data : [];
  } catch (err) {
    // در صورت خطا، تلاش برای fallback
    try {
      const fb = await axios.get(fallbackUrl, { headers: authHeader() });
      return fb.data;
    } catch (e) {
      const msg = err?.response?.data?.message || err?.message || 'خطا در دریافت لیست درس‌ها';
      throw new Error(msg);
    }
  }
};

/**
 * getAdminAbsents({ date, session = 1, lessonId? })
 * اندپوینت تجمیعی برای Admin: GET /api/attendance/admin/absents?date=YYYY-MM-DD&session=1(&lessonId=8)
 * نتیجه مورد انتظار: { date, sessionIndex, count, items: [...] }
 */
export const getAdminAbsents = async ({ date, session = 1, lessonId } = {}) => {
  if (!date) throw new Error('date is required');
  const qp = `?date=${encodeURIComponent(date)}&session=${encodeURIComponent(session)}${lessonId ? `&lessonId=${encodeURIComponent(lessonId)}` : ''}`;
  const adminUrl = `${BASE}/api/attendance/admin/absents${qp}`;
  try {
    const res = await axios.get(adminUrl, { headers: authHeader(), validateStatus: s => s < 500 });
    if (res.status === 200) return res.data;
    // اگر سرور این اندپوینت را نداشت، پیام خطای قابل فهم برگردان
    throw new Error(res?.data?.error || res?.data?.message || `admin absents request failed: ${res.status}`);
  } catch (err) {
    const msg = err?.response?.data?.message || err?.message || 'خطا در گرفتن غایبین ادمین';
    throw new Error(msg);
  }
};